import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import Routes from "../../routes";
import LandingHeader from "../LandingHomePage/LandingHeader/LandingHeader";
import { getCookie, setCookie, handleSessionExpiry, handleSilentSessionReset } from "../../utils/utils";
import { LogoutInvalidToken } from "../../utils/utils";
import { StoreContext } from "../ContextStore/StoreContextProvider";
import MicroserviceFetchUtil from "../../common/microservice-fetch-util";
import BrandingLoader from "../Brandingloader/BrandingLoader";

export const ROUTES_WITHOUT_HEADER = ["/login", "/signup", "/forgotpassword"];

const Main = () => {
  const {
    userDetails,
    updateUserDetails,
    selectedCountryCode,
    updateCountryCode,
  } = useContext(StoreContext);
  const [isLoading, setIsLoading] = useState(false);
  const isProfileFetchInFlightRef = useRef(false);
  const lastProfileFetchKeyRef = useRef('');
  const { pathname } = useLocation();

  const { hasHeader } = useMemo(() => {
    return {
      hasHeader: !ROUTES_WITHOUT_HEADER.some(route =>
        route instanceof RegExp
          ? route.test(pathname)
          : pathname.search(route) !== -1
      ),
    };
  }, [pathname]);

  useEffect(() => {
    const idToken = getCookie("idToken");
    const refreshToken = getCookie("refreshToken");

    if (idToken === null && !ROUTES_WITHOUT_HEADER.includes(pathname)) {
      if (refreshToken) {
        LogoutInvalidToken();
      } else {
        // Both tokens missing - consistent session expiry handling with alert
        handleSessionExpiry();
      }
    }
  }, [pathname]);

  useEffect(() => {
    const idToken = getCookie("idToken");
    const countryCode = localStorage.getItem("countryCode");

    if (idToken === null) {
      return;
    }

    const requestKey = `${idToken}:${countryCode}`;
    if (lastProfileFetchKeyRef.current === requestKey && userDetails) {
      return;
    }

    if (isProfileFetchInFlightRef.current) {
      return;
    }

    isProfileFetchInFlightRef.current = true;
    setIsLoading(true);
    const userEmailObj = { email: localStorage.getItem("email") };
    MicroserviceFetchUtil.post(
      "fb/users/profile",
      {
        customerToken: idToken,
        countryCode,
      },
      userEmailObj
    )
      .then(response => {
        // Check for 401 (invalid token) - show expiry alert
        if (response?.statusCode === 401) {
          LogoutInvalidToken();
          return;
        }
        
        // Check for 500 + PERMISSION_DENIED (API auth issue) - silent redirect
        if (response?.statusCode >= 500 && 
            response?.errors?.errorPayload?.error?.status === "PERMISSION_DENIED") {
          handleSilentSessionReset();
          return;
        }
        
        if (response.statusCode === 200 && response["response-data"]) {
          lastProfileFetchKeyRef.current = requestKey;
          updateUserDetails(response["response-data"]);
          if (selectedCountryCode !== countryCode) {
            updateCountryCode(countryCode);
          }
          setCookie("countryCode", countryCode);
        } else if (response.statusCode >= 400) {
          console.error("Unexpected response while fetching user profile:", response);
        }
      })
      .catch(error => {
        if (error?.code === 'NETWORK_NO_RESPONSE') {
          LogoutInvalidToken();
          return;
        }
        console.error("Error while fetching user profile:", error);
      })
      .finally(() => {
        isProfileFetchInFlightRef.current = false;
        setIsLoading(false);
      });
  }, [selectedCountryCode, updateCountryCode, updateUserDetails, userDetails]);


  return (
    <>
      {isLoading ? (
        <BrandingLoader />
      ) : (
        <div
          style={{
            minHeight: "100Vh",
            maxHeight: "max-content",
            backgroundColor: window.innerWidth <= 768 ? "#FFFFF" : "#FAFCFF",
          }}
        >
          {hasHeader && <LandingHeader />}
          <Routes />
        </div>
      )}
    </>
  );
};

export default Main;

